<template>
  <div :id="'model-live-box-' + video.id" class="video-container">
    <ClientOnly>
      <vue-plyr ref="plyr" :options="plyrOptions">
        <video :autoplay="false" controls playsinline ref="videoRef">
          <source src="" type="video/mp4" />
        </video>
      </vue-plyr>
      <div v-if="showIcon" class="play-icon" @click="playVideo">
        <font-awesome :icon="['fas', 'circle-play']" size="3x" />
      </div>
      <i class="watermark">{{ storeUser.user.id }}</i>
      <button class="fullscreen-button" @click="toggleFullscreen()">
        <i class="icon-resize-full"></i>
      </button>
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import { gtmViewVideo, gtmPlayVideo, gtmFinishVideo } from "~/util/gtm-videos";
import VuePlyr from "vue-plyr";
import Hls from "hls.js";
import { hlsConfigComCredenciais } from "~/util/hls-config";
import "vue-plyr/dist/vue-plyr.css";
import translateModel from "@/tradutors/model";

const translate = computed(() =>
  route.params.language == "pt-br"
    ? translateModel.portugues
    : translateModel.ingles
);

const plyr = ref<HTMLVideoElement | null>(null);

const hls = new Hls(hlsConfigComCredenciais);

const props = defineProps(["video", "thumbVideo"]);

useFullscreenLandscape();

const videoSrc = ref("");
const videoRef = ref<HTMLVideoElement | null>(null);
const interval = ref<any>(null);
const showIcon = ref(true);

const plyrOptions = ref({
  controls: [
    "play-large", // The large play button in the center
    "restart", // Restart playback
    "rewind", // Rewind by the seek time (default 10 seconds)
    "play", // Play/pause playback
    "fast-forward", // Fast forward by the seek time (default 10 seconds)
    "progress", // The progress bar and scrubber for playback and buffering
    "current-time", // The current time of playback
    "duration", // The full duration of the media
    "mute", // Toggle mute
    "volume", // Volume control
    "captions", // Toggle captions
    "settings",
  ],
  fullscreen: {
    enabled: true,
    iosNative: true,
  },
  settings: ["quality", "speed"],
  debug: false,
  quality: {
    options: [],
    forced: true,
    default: 360,
  },
});
const isVisible = ref(false);
const isInPictureInPictureEnabled = ref(false);
const route = useRoute();
const storeUser = userStore();

watch(() => props.video, async () => {
  showIcon.value = true;  
  videoSrc.value = await getVideo();
});

watch(
  () => videoSrc.value,
  () => {
    if (videoSrc.value) {
      if (videoRef.value) {
        videoRef.value.src = videoSrc.value;
        videoRef.value.poster = props.thumbVideo ?? '';
      }
    }
  }
);

watch(videoRef, async () => {
  videoSrc.value = await getVideo();
  if (plyr.value) {
    plyr.value.player.on("ready", () => {
      gtmViewVideo("live", "completo", route.params.name as string);
      showIcon.value = true;
    });
    plyr.value.player.on("play", () => {
      gtmPlayVideo("live", "completo", route.params.name as string);
      showIcon.value = false;
    });
    plyr.value.player.on("ended", () => {
      gtmFinishVideo("live", "completo", route.params.name as string);
      showIcon.value = true;
    });
    if (videoRef.value) {
      videoRef.value.addEventListener("enterpictureinpicture", () => {
        isInPictureInPictureEnabled.value = true;
        videoRef.value.src = videoSrc.value = "";
      });
      videoRef.value.addEventListener("leavepictureinpicture", async () => {
        isInPictureInPictureEnabled.value = false;
        videoSrc.value = await getVideo();
      });
      videoRef.value.poster = props.thumbVideo ?? ''; 
    }
  }
});

onMounted(async () => {
  checkVisibility();
  interval.value = setInterval(checkVisibility, 1000);
});
onUnmounted(() => {
  clearInterval(interval.value);

  if (hls) {
    hls.destroy();
  }
});
async function getVideo() {
  const videosUrl = props.video.path;

  if (Hls.isSupported() && videoRef.value) {
    hls.loadSource(videosUrl);
    hls.attachMedia(videoRef.value);
    return "";
  } else if (
    videoRef.value &&
    videoRef.value.canPlayType("application/vnd.apple.mpegurl")
  ) {
    return videosUrl;
  }

  return videosUrl;
}

function checkVisibility() {
  const icon: any = document.querySelector(".watermark");
  isVisible.value = icon && icon.offsetParent !== null;

  let computedStyle = icon ? window.getComputedStyle(icon) : null;
  let animationName = computedStyle ? computedStyle.animationName : null;

  if (
    !isVisible.value ||
    animationName == "none" ||
    computedStyle?.animationDuration != "15s" ||
    computedStyle.animationIterationCount != "infinite"
  )
    videoSrc.value = "";
  if (icon) {
    icon.style =
      "position: absolute;left: 3%;top: 3%;color: #cccccc !important;opacity: 0.2 !important;visibility: visible !important;font-size: 1rem !important";
    icon.class = "watermark";
  }
}

function iOS() {
  return [
    "iPad Simulator",
    "iPhone Simulator",
    "iPod Simulator",
    "iPad",
    "iPhone",
    "iPod",
  ].includes(navigator?.userAgentData?.platform || navigator?.platform);
}
function firefox() {
  return navigator?.userAgent.includes("Firefox");
  // return false
}
function toggleFullscreen() {
  if (!iOS()) {
    if (!document.fullscreenElement) {
      const videoBox = document.getElementById(
        "model-live-box-" + props.video.id
      );
      if (videoBox) {
        videoBox.requestFullscreen();
        videoBox.classList.toggle("full");
      }
    } else if (document.exitFullscreen) {
      const videoBox = document.getElementById(
        "model-live-box-" + props.video.id
      );
      if (videoBox) {
        videoBox.classList.toggle("full");
      }
      document.exitFullscreen();
    }
  } else {
    const videoBox = document.getElementById(
      "model-live-box-" + props.video.id
    );
    if (videoBox) {
      videoBox.classList.toggle("video-container-ios");
    }
  }
}

function playVideo() {
  if (plyr.value) {
    plyr.value.player.play();
  }
}

// Para as tools WebMCP do player (Lives.vue): o Plyr, para play, pausa e tempo.
defineExpose({ player: () => (plyr.value as any)?.player ?? null });
</script>

<style scoped>
.full >>> .plyr video {
  max-height: 100vh !important;
}

.plyr video {
  width: 100%;  
  aspect-ratio: 16 / 9;
  object-fit: contain;
}

.plyr:fullscreen video {
    width: 100vw !important;
    aspect-ratio: 16 / 9;    
    object-fit: contain;
}

.video-container {
  position: relative;
}

.video-container-ios {
  position: fixed; /* Fixa o contêiner na tela */
  top: 0;
  left: 0;
  width: 100vw; /* Largura total da viewport */
  height: 100vh; /* Altura total da viewport */
  background-color: black; /* Fundo preto para simular fullscreen */
  z-index: 9999; /* Coloca o contêiner acima de outros elementos */
}

.video-container-ios iframe {
  height: 100vh;
}

.full {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.full iframe {
  height: 100vh;
}

.fullscreen-button {
  position: absolute;
  top: 3%;
  right: 1%;
  z-index: 10;
}

.watermark {
  position: absolute;
  left: 3%;
  top: 3%;
  color: #cccccc;
  opacity: 0.2;
  visibility: visible !important;
  animation: 15s moving-watermark infinite;
}

.play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  /* Tamanho do ícone de reprodução */
  color: white;
  /* Cor do ícone de reprodução */
  cursor: pointer;
  z-index: 1;
}

@keyframes moving-watermark {
  0% {
    left: 3%;
    top: 50%;
  }

  25% {
    left: 45%;
    top: 3%;
  }

  50% {
    left: 45%;
    top: 90%;
  }

  75% {
    left: 93%;
    top: 50%;
  }

  100% {
    left: 3%;
    top: 50%;
  }
}

i[class*="icon-"]:before {
  color: #ffffff;
  font-size: 25px;
}

@media screen and (max-width: 970px) {
  .video-container {
    width: 98vw;
  }
  .video-container-ios {
    width: 100vw;
  }

  @keyframes moving-watermark {
    0% {
      left: 3%;
      top: 50%;
    }
    25% {
      left: 45%;
      top: 3%;
    }
    50% {
      left: 45%;
      top: 85%;
    }
    75% {
      left: 85%;
      top: 50%;
    }
    100% {
      left: 3%;
      top: 50%;
    }
  }

}
</style>
